import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Avatar,
  Paper,
  Grid,
  Card,
  Box,
  TextField,
  Pagination,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import EmptyData from "../../components/EmptyData";
import { useSelector } from "react-redux";
import ButtonContainer from "Utils/approvedRejectDeleteButton.";
import apiClient from "../../Utils/apiClient";
import socketService from "../../socket/newsocketService";

const AdminAccess = () => {
  const getUserState = useSelector((state) => state?.data?.getUserState);
  const [allData, setAllData] = useState([]);
  const [checkSuperAdmin, setCheckSuperAdmin] = useState(null);
  const [adminState, setAdminState] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const pageSize = 10;
  const navigate = useNavigate();

  const currentAdmin = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || {};
    } catch (error) {
      return {};
    }
  }, []);

  const getData = useCallback(async () => {
    try {
      const currentAdminState = currentAdmin?.state || currentAdmin?.userState || "";
      const isSuperAdmin = Boolean(currentAdmin?.superAdmin);

      setCheckSuperAdmin(isSuperAdmin);
      setAdminState(currentAdminState);

      const params = {
        page: currentPage,
        limit: pageSize,
        adminOnly: true,
      };

      if (!isSuperAdmin && currentAdminState) {
        params.state = currentAdminState;
      }

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const response = await apiClient.get("/admins", { params });
      const payload = response?.data ?? response ?? [];
      const pagination = response?.pagination ?? {};
      const users = Array.isArray(payload) ? payload : [];
      const adminUsers = users.filter((item) => {
        const accessValue = item?.adminAccess;
        const isAdminLike =
          item?.superAdmin === true ||
          item?.isOperator === true ||
          item?.role === "operator" ||
          accessValue === "Approved" ||
          accessValue === "Pending" ||
          accessValue === "Rejected";

        return isAdminLike;
      });

      const filteredData = isSuperAdmin
        ? adminUsers
        : adminUsers.filter(
            (item) => String(item?.state || item?.userState || "") === String(currentAdminState || ""),
          );

      const resolvedTotalPages = Number(
        pagination?.totalPages || Math.ceil((pagination?.total ?? filteredData.length) / pageSize) || 1,
      );

      setAllData(filteredData);
      setTotalPages(Math.max(1, Number.isFinite(resolvedTotalPages) ? resolvedTotalPages : 1));
      setTotalUsers(Number(pagination?.total ?? filteredData.length));
    } catch (error) {
      console.log("[AdminAccess] fetch error", error);
      setAllData([]);
      setTotalPages(1);
      setTotalUsers(0);
    }
  }, [currentAdmin, currentPage, searchTerm, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, getUserState]);

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (!user) {
      navigate("/authentication/sign-in");
      return;
    }

    const currentUser = JSON.parse(user);
    socketService.disconnect();
    void socketService
      .initializeSocket(currentUser?._id || currentUser?.userId || currentUser?.key || null, currentUser?.state || currentUser?.userState || "", {
        isSuperAdmin: Boolean(currentUser?.superAdmin),
        web: "true",
      })
      .then(() => {
        console.log("[AdminAccess] socket connected");
      })
      .catch((error) => {
        console.log("[AdminAccess] socket connect failed", error);
      });

    void getData();

    const handleDisconnect = () => console.log("[AdminAccess] socket disconnected");
    socketService.on("disconnect", handleDisconnect);

    return () => {
      socketService.off("disconnect", handleDisconnect);
      socketService.disconnect();
    };
  }, [getData, navigate, getUserState]);

  const handleApproveReject = async (user, action) => {
    try {
      const userId = user?._id || user?.id || user?.userId || user?.key;
      if (!userId) {
        toast.error("User ID is missing");
        return;
      }

      const adminAccessValue = action === "approved" ? "Approved" : "Rejected";

      await apiClient.put(`/admins/${userId}`, {
        adminAccess: adminAccessValue,
        applicationReviewed: true,
      });

      toast.success("Admin access permission updated successfully!");
      await getData();
    } catch (error) {
      console.log("[AdminAccess] approve/reject error", error);
      toast.error(error?.message || "Failed to update admin access.");
    }
  };

  const handleDelete = async (user) => {
    try {
      const userId = user?._id || user?.id || user?.userId || user?.key;
      if (!userId) {
        toast.error("User ID is missing");
        return;
      }

      await apiClient.delete(`/admins/${userId}`);
      toast.success("Admin user deleted successfully!");
      await getData();
    } catch (error) {
      console.log("[AdminAccess] delete error", error);
      toast.error(error?.message || "Failed to delete admin.");
    }
  };

  const filteredData = allData.filter((item) => {
    if (!searchTerm) return true;

    const term = searchTerm.toLowerCase();
    const fullName = `${item?.firstName || item?.userName || ""} ${item?.lastName || ""}`.toLowerCase();
    const email = String(item?.userEmail || item?.email || "").toLowerCase();
    const phone = String(item?.userPhone || item?.phoneNumber || "").toLowerCase();

    return fullName.includes(term) || email.includes(term) || phone.includes(term);
  });

  const paginatedData = filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <DashboardLayout>
      <MDBox pt={9} pb={3}>
        <Box display="flex" justifyContent="flex-end" alignItems="center" mb={5} px={2}>
          <TextField
            label="Search by name, email, or phone"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: 300 }}
          />
        </Box>
        <Grid container spacing={6} style={{ position: "relative", minHeight: "60vh" }}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                sx={{
                  textAlign: "center",
                }}
              >
                <ToastContainer />
                <MDTypography
                  variant="h6"
                  color="white"
                  sx={{
                    fontWeight: "bold",
                    fontSize: "1.5rem",
                  }}
                >
                  {checkSuperAdmin
                    ? getUserState === ""
                      ? "ALL STATE "
                      : `${getUserState?.toUpperCase()} STATE `
                    : `${String(adminState || "").toUpperCase()} STATE `}
                  ADMIN ACCESS DASHBOARD
                </MDTypography>
              </MDBox>

              {paginatedData.length > 0 ? (
                <MDBox>
                  <TableContainer component={Paper}>
                    <Table aria-label="user table">
                      <TableBody>
                        <TableRow>
                          <TableCell style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }}>
                            SR
                          </TableCell>
                          <TableCell style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }}>
                            Name
                          </TableCell>
                          <TableCell
                            style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }}
                            align="center"
                          >
                            Email
                          </TableCell>
                          <TableCell
                            style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }}
                            align="center"
                          >
                            Phone
                          </TableCell>
                          <TableCell
                            style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }}
                            align="center"
                          >
                            State
                          </TableCell>
                          <TableCell
                            style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }}
                            align="center"
                          >
                            Application Reviewed
                          </TableCell>
                          <TableCell
                            style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }}
                            align="center"
                          >
                            Admin Access
                          </TableCell>
                          <TableCell
                            style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }}
                            align="center"
                          >
                            Action
                          </TableCell>
                        </TableRow>
                      {paginatedData
                        .map((item, index) => {
                          let rowColor = "";
                          if (item?.adminAccess === "Approved") {
                            rowColor = "lightgreen";
                          } else if (item?.adminAccess === "Pending") {
                            rowColor = "lightyellow";
                          } else if (item?.adminAccess === "Rejected") {
                            rowColor = "lightcoral";
                          }

                          return (
                            <TableRow key={item?._id || item?.id || index} sx={{ height: "auto", backgroundColor: rowColor }}>
                              <TableCell align="center" sx={{ padding: "6px" }}>
                                {(currentPage - 1) * pageSize + index + 1}
                              </TableCell>
                              <TableCell sx={{ padding: "6px", fontSize: "14px" }} component="th" scope="row">
                                <Avatar
                                  src={item?.userImage || item?.imageUrl}
                                  alt={item?.firstName || item?.userName || "Admin"}
                                />
                                {item?.firstName || item?.userName || ""} {item?.lastName || ""}
                              </TableCell>
                              <TableCell align="center" sx={{ padding: "6px", fontSize: "14px" }}>
                                {item?.userEmail || item?.email || ""}
                              </TableCell>
                              <TableCell align="center" sx={{ padding: "6px", fontSize: "14px" }}>
                                {item?.userPhone || item?.phoneNumber || ""}
                              </TableCell>
                              <TableCell align="center" sx={{ padding: "6px", fontSize: "14px" }}>
                                {item?.state || item?.userState || ""}
                              </TableCell>
                              <TableCell align="center" sx={{ padding: "6px", fontSize: "14px" }}>
                                {item?.applicationReviewed ? <p>Reviewed</p> : <p>Pending</p>}
                              </TableCell>
                              <TableCell align="center" sx={{ padding: "6px", fontSize: "14px" }}>
                                {item?.adminAccess || "Pending"}
                              </TableCell>
                              <TableCell align="center" sx={{ padding: "6px", fontSize: "14px" }}>
                                <ButtonContainer
                                  notification={item}
                                  handleApproveReject={handleApproveReject}
                                  handleDelete={handleDelete}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box display="flex" justifyContent="space-between" alignItems="center" px={3} py={2}>
                    <MDTypography variant="button" color="text">
                      Showing {filteredData.length ? (currentPage - 1) * pageSize + 1 : 0} - {Math.min(currentPage * pageSize, filteredData.length)} of {totalUsers || filteredData.length}
                    </MDTypography>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={(event, value) => setCurrentPage(value)}
                      color="primary"
                    />
                  </Box>
                </MDBox>
              ) : (
                <EmptyData />
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
};

export default AdminAccess;
